/* eslint-disable @typescript-eslint/no-explicit-any -- legacy provider payload contracts are intentionally open-ended */
/**
 * TypeScript types for LangChain integration
 * Database types for AI providers, models, configurations, and knowledge base
 */

// ============================================================================
// AI Providers & Models
// ============================================================================

export interface AIProvider {
  id: string
  name: string // 'openai', 'anthropic', 'google', 'mistral'
  display_name: string
  is_enabled: boolean
  api_key_encrypted?: string
  base_url?: string
  created_at: string
  updated_at: string
}

export type AIModelType = 'chat' | 'embedding' | 'image' | 'audio'

export interface AIModel {
  id: string
  provider_id: string
  model_name: string // 'gpt-4o-mini', 'claude-3-5-sonnet-20241022', etc.
  display_name: string
  model_type: AIModelType
  is_enabled: boolean

  // Capabilities
  supports_streaming: boolean
  supports_function_calling: boolean
  supports_vision: boolean

  // Cost information (microUSD per 1M tokens; 1 microUSD = $0.000001)
  cost_input_per_1m_microusd?: number
  cost_output_per_1m_microusd?: number

  // Default model parameters (temperature, max_tokens, reasoning_effort, etc.)
  config: Record<string, unknown>

  created_at: string
  updated_at: string

  // Relations (when joined)
  provider?: AIProvider
}

// ============================================================================
// Per-Model Configuration
// ============================================================================

/**
 * Per-model parameter overrides for primary and fallback models.
 * Stored in global_ai_config.model_config / tenant_ai_configs.model_config JSONB.
 * Allows different temperature/max_tokens (and arbitrary params) per model role.
 */
export interface ModelConfig {
  primary?: ModelConfigEntry
  fallback?: ModelConfigEntry
}

export interface ModelConfigEntry {
  temperature?: number
  max_tokens?: number
  [key: string]: unknown
}

// ============================================================================
// AI Configurations
// ============================================================================

/**
 * Processing configuration for AI use cases
 * Stored in JSONB column `processing_config`
 */
export interface ProcessingConfig {
  // RAG settings — defaults live in RAG_DEFAULTS (lib/ai/knowledge-base/constants.ts)
  rag_threshold?: number // Minimum similarity score (0-1)
  rag_limit?: number // Max chunks to retrieve

  // Knowledge Base settings
  include_knowledge?: boolean // Whether to include KB context, default true
  store_original_files?: boolean // Store uploaded files in storage

  // email_classification: categories the classifier must choose from
  classifier_labels?: string[]

  // Other processing settings
  [key: string]: any
}

/**
 * Scope types for per-agent AI configurations
 */
export type AIScopeType = 'email_agent' | 'voice_agent' | 'message_agent'

/**
 * Scope configuration for per-agent AI settings
 */
export interface AIScope {
  type: AIScopeType
  id: string
}

export interface TenantAIConfig {
  id: string
  tenant_id: string
  use_case: string // 'email_classification', 'email_auto_reply', 'kb_email_analysis', etc.

  // Scope support (per-agent configs)
  scope_type?: AIScopeType | null // 'email_agent', 'voice_agent', null = tenant default
  scope_id?: string | null // Agent UUID when scope_type is set

  primary_model_id?: string
  fallback_model_id?: string

  // Per-model parameter overrides (JSON with arbitrary params per role)
  model_config?: ModelConfig

  // Prompt management
  system_prompt?: string
  prompt_template?: string

  // Processing configuration (RAG settings, etc.)
  processing_config?: ProcessingConfig

  is_enabled: boolean

  created_at: string
  updated_at: string

  // Relations (when joined)
  primary_model?: AIModel
  fallback_model?: AIModel
}

export interface GlobalAIConfig {
  id: string
  use_case: string

  primary_model_id?: string
  fallback_model_id?: string

  // Per-model parameter overrides (JSON with arbitrary params per role)
  model_config?: ModelConfig

  system_prompt?: string
  prompt_template?: string

  // Processing configuration (RAG settings, etc.)
  processing_config?: ProcessingConfig

  is_enabled: boolean

  created_at: string
  updated_at: string

  // Relations (when joined)
  primary_model?: AIModel
  fallback_model?: AIModel
}

// ============================================================================
// Knowledge Base
// ============================================================================

export interface KnowledgeBaseDocument {
  id: string
  tenant_id: string

  title: string
  content: string
  content_type: string // 'text/plain', 'text/markdown', 'text/html'

  source_url?: string
  source_type: string // 'manual', 'website', 'file', 'api', 'email_analysis'
  metadata: Record<string, any>

  tags: string[]
  category?: string

  is_active: boolean
  is_default: boolean

  created_at: string
  updated_at: string
}

export interface KnowledgeBaseEmbedding {
  id: string
  document_id: string
  tenant_id: string

  chunk_index: number
  chunk_text: string
  chunk_metadata: Record<string, any>

  embedding: number[] // vector(1536) represented as array
  embedding_model_id?: string

  created_at: string

  // Relations (when joined)
  embedding_model?: AIModel
}

export interface KnowledgeChunk {
  id: string
  document_id: string
  chunk_index: number
  chunk_text: string
  chunk_metadata: Record<string, any>
  similarity_score: number
  document_title: string
  document_category?: string
  document_tags: string[]
}

// ============================================================================
// Logging & Analytics
// ============================================================================

export interface AIRequestLog {
  id: string
  tenant_id: string

  model_id?: string
  provider_name: string
  model_name: string

  use_case: string

  system_prompt?: string
  user_prompt: string
  assistant_response?: string

  tools_used?: any[]
  function_calls?: any[]

  input_tokens?: number
  output_tokens?: number
  total_tokens?: number
  execution_time_ms?: number

  estimated_cost?: number

  success: boolean
  error_message?: string

  context: Record<string, any>

  created_at: string

  // Relations (when joined)
  model?: AIModel
}

// ============================================================================
// Prompt Library
// ============================================================================

export interface PromptLibrary {
  id: string
  tenant_id?: string // null = global prompt

  name: string
  description?: string
  use_case: string

  version: number

  system_prompt?: string
  user_prompt_template: string

  variables: string[] // Array of variable names used in template

  is_active: boolean
  test_results?: Record<string, any>

  created_by?: string
  created_at: string
  updated_at: string

  // Relations (when joined)
  creator?: {
    uuid: string
    email: string
    full_name?: string
  }
}

// ============================================================================
// Service Types
// ============================================================================

/**
 * Who pays for the call. `'platform'` skips the AI-access gate and the wallet
 * deduction so a platform onboarding operation is not blocked by an empty
 * tenant wallet. It is deliberately narrow: only explicitly allowlisted use
 * cases are accepted, and config comes from `global_ai_config` alone.
 */
export type AIBillingMode = 'tenant' | 'platform'

export interface LangChainServiceConfig {
  tenantId: string
  useCase: string
  fallbackEnabled?: boolean
  /** Defaults to `'tenant'`. See {@link AIBillingMode} before widening its use. */
  billingMode?: AIBillingMode
  /**
   * Optional scope for per-agent configs.
   * When provided, the service will load scoped config first, then merge with tenant default.
   * Priority: scoped config → tenant default → global default
   */
  scope?: AIScope
  /**
   * KB document IDs from agent config (config.knowledge_bases).
   * undefined or [] = no KB documents.
   */
  knowledgeBaseIds?: string[]
}

export interface ChatOptions {
  systemMessage?: string
  maxToolCalls?: number
  streaming?: boolean
  /**
   * Budget for the whole reply — every candidate and tool-loop turn together.
   * Defaults to `LLM_SEQUENCE_TIMEOUT_MS`; pass a tighter one where a human is
   * watching (operator test chat) rather than a worker.
   */
  sequenceTimeoutMs?: number
}

export interface RAGOptions {
  searchLimit?: number
  similarityThreshold?: number
  category?: string
  tags?: string[]
  includeMetadata?: boolean
}

export interface ChatResult {
  response: string
  usage: {
    inputTokens: number
    outputTokens: number
    totalTokens: number
  }
  executionTime: number
  model: {
    id: string
    name: string
    provider: string
  }
  toolCalls?: any[]
}

// ============================================================================
// Provider-specific types
// ============================================================================

export type ProviderName = 'openai' | 'anthropic' | 'google' | 'mistral'

export interface ProviderConfig {
  apiKey: string
  baseUrl?: string
  additionalConfig?: Record<string, any>
}

export interface ModelUsageStats {
  total_requests: number
  successful_requests: number
  failed_requests: number
  total_tokens: number
  total_cost: number
  avg_latency_ms: number
  period_start: string
  period_end: string
}

// ============================================================================
// Use Case Constants
// ============================================================================

export const AI_USE_CASES = {
  EMAIL_CLASSIFICATION: 'email_classification',
  EMAIL_AUTO_REPLY: 'email_auto_reply',
  AUTO_REPLY_PROMPT_GENERATOR: 'auto_reply_prompt_generator',
  KB_EMAIL_ANALYSIS: 'kb_email_analysis',
  KB_SPREADSHEET_SUMMARY: 'kb_spreadsheet_summary',
  VOICE_AGENT: 'voice_agent',
  MESSAGE_AGENT_REPLY: 'message_agent_reply',
  ROLE_PROMPT_COMPOSITION: 'role_prompt_composition',
  ONBOARDING_PROMPT_PERSONALIZATION: 'onboarding_prompt_personalization',
  CONVERSATION_QUALITY_REVIEW: 'conversation_quality_review',
  CONVERSATION_QUALITY_SUGGESTION: 'conversation_quality_suggestion',
  CONVERSATION_QUALITY_TOPIC_MERGE: 'conversation_quality_topic_merge',
  CONVERSATION_QUALITY_CHANGE_CHECK: 'conversation_quality_change_check',
  CUSTOM: 'custom',
} as const

export type AIUseCase = (typeof AI_USE_CASES)[keyof typeof AI_USE_CASES]

// ============================================================================
// Model Testing Types
// ============================================================================

export interface ModelTestParams {
  // Option A: test unsaved model by provider + model name
  providerId?: string
  modelName?: string
  modelType?: AIModelType
  // Whether to bind a tool during the probe.
  supportsFunctionCalling?: boolean
  // Option B: test saved model by ID
  modelId?: string
  // Explicit model params to test (camelCase, like model_config). When provided,
  // overrides the DB model.config for the probe — lets the UI validate the exact
  // params being edited before they're saved.
  config?: Record<string, unknown>
  // Input for testing
  message?: string  // for chat models
  text?: string     // for embedding models
}

export interface ModelTestResult {
  success: boolean
  // Chat response
  response?: string
  // Embedding result
  embedding?: {
    dimensions: number
    preview: number[]  // first 10 values
    norm: number       // L2 norm
  }
  // Model info
  model: {
    name: string
    provider: string
  }
  // Timing
  timing: {
    durationMs: number
  }
  // How the probe was sent. Derived, not chosen by the user, so a bare
  // pass/fail says little without it.
  probe?: {
    /** OpenAI only. */
    transport?: 'chat_completions' | 'responses'
    toolsBound: boolean
    reasoningEffort?: string
  }
  // Error (if failed)
  error?: {
    type: 'api_key_invalid' | 'model_not_found' | 'rate_limit' | 'unsupported_parameter' | 'provider_error' | 'unsupported_type' | 'invalid_params'
    message: string
    details?: string
  }
}
